// Transform API data for Highcharts with chart level grouping

export function transformMaxAvailData(data, aggregationLevel, chartLevel = 'system', selectedVariables = ['generation_max_availability']) {
  if (!data || data.length === 0) return [];

  // If chart level is 'system', return single grouped dataset (V1 behavior)
  if (chartLevel === 'system') {
    return transformSingleChart(data, aggregationLevel, selectedVariables);
  }

  // Group data by chart level, then by aggregation level
  return transformMultipleCharts(data, aggregationLevel, chartLevel, selectedVariables);
}

function transformSingleChart(data, aggregationLevel, selectedVariables = ['generation_max_availability']) {
  const keyField = {
    duid: 'duid',
    station: 'stationid',
    portfolio: 'portfolioid',
    fuel_type: 'fuel_type'
  }[aggregationLevel];

  // Create series for each variable
  const result = [];

  selectedVariables.forEach(variableKey => {
    const seriesMap = {};
    const dataField = variableKey === 'generation_pasa_availability' ? 'pasa_avail' : 'maxavail';

    data.forEach(row => {
      const entityKey = row[keyField];
      if (!entityKey) return;

      if (!seriesMap[entityKey]) {
        const baseName = aggregationLevel === 'station' && row.station_name ? row.station_name : entityKey;
        const varName = variableKey === 'generation_pasa_availability' ? ' (PASA)' : ' (Max)';
        seriesMap[entityKey] = {
          name: selectedVariables.length > 1 ? baseName + varName : baseName,
          data: [],
          region: row.region_id,
          fuelType: row.fuel_type,
          portfolio: row.portfolioid
        };
      }

      const timestamp = new Date(row.interval_datetime).getTime();
      seriesMap[entityKey].data.push([timestamp, row[dataField] || 0]);
    });

    Object.values(seriesMap).forEach(series => {
      series.data.sort((a, b) => a[0] - b[0]);
    });

    result.push(...Object.values(seriesMap));
  });

  return result;
}

function transformMultipleCharts(data, aggregationLevel, chartLevel, selectedVariables = ['generation_max_availability']) {
  // Determine chart grouping field
  const chartGroupField = {
    region: 'region_id',
    fuel_type: 'fuel_type',
    portfolio: 'portfolioid',
    station: 'stationid',
    duid: 'duid'
  }[chartLevel];

  // Determine series key field based on aggregation level
  const seriesKeyField = {
    duid: 'duid',
    station: 'stationid',
    portfolio: 'portfolioid',
    fuel_type: 'fuel_type'
  }[aggregationLevel];

  // Group data first by chart level, then by aggregation level, then by variable
  const chartGroups = {};

  selectedVariables.forEach(variableKey => {
    const dataField = variableKey === 'generation_pasa_availability' ? 'pasa_avail' : 'maxavail';

    data.forEach(row => {
      const chartKey = row[chartGroupField];
      const seriesKey = row[seriesKeyField];

      if (!chartKey || !seriesKey) return;

      // Initialize chart group
      if (!chartGroups[chartKey]) {
        chartGroups[chartKey] = {
          chartTitle: getChartTitle(chartLevel, chartKey, row),
          series: {}
        };
      }

      // Create unique series key for variable
      const fullSeriesKey = selectedVariables.length > 1 ? `${seriesKey}_${variableKey}` : seriesKey;

      // Initialize series within chart group
      if (!chartGroups[chartKey].series[fullSeriesKey]) {
        const baseName = aggregationLevel === 'station' && row.station_name ? row.station_name : seriesKey;
        const varName = variableKey === 'generation_pasa_availability' ? ' (PASA)' : ' (Max)';
        chartGroups[chartKey].series[fullSeriesKey] = {
          name: selectedVariables.length > 1 ? baseName + varName : baseName,
          data: [],
          region: row.region_id,
          fuelType: row.fuel_type,
          portfolio: row.portfolioid
        };
      }

      const timestamp = new Date(row.interval_datetime).getTime();
      chartGroups[chartKey].series[fullSeriesKey].data.push([timestamp, row[dataField] || 0]);
    });
  });

  // Convert to array format and sort data points
  const result = Object.entries(chartGroups).map(([chartKey, chartData]) => {
    const series = Object.values(chartData.series);
    series.forEach(s => s.data.sort((a, b) => a[0] - b[0]));

    return {
      chartKey,
      chartTitle: chartData.chartTitle,
      series
    };
  });

  return result;
}

function getChartTitle(chartLevel, key, row) {
  switch (chartLevel) {
    case 'region':
      return `Region: ${key}`;
    case 'fuel_type':
      return `Fuel Type: ${key}`;
    case 'portfolio':
      return `Portfolio: ${key}`;
    case 'station':
      return `Station: ${row.station_name || key}`;
    case 'duid':
      return `DUID: ${key}`;
    default:
      return key;
  }
}

export function getEntityKey(row, aggregationLevel) {
  const keyFields = {
    duid: 'duid',
    station: 'stationid',
    portfolio: 'portfolioid',
    fuel_type: 'fuel_type'
  };
  return row[keyFields[aggregationLevel]];
}
